import type { EntityAdapter, EntityState } from '@ngrx/entity';
import { createEntityAdapter } from '@ngrx/entity';
import { on } from '@ngrx/store';
import { createImmerReducer } from 'ngrx-immer/store';

import type { NormalizedGridEntity } from '@web/types';

import { getVariantMapList } from './grids.models';

import GridsActions from './grids.actions';
import WidgetsLegacyActions, { WidgetsActions } from '../widgets/widgets.actions';

export const GRIDS_FEATURE_KEY = 'grids';

export type GridsState = EntityState<NormalizedGridEntity>;

export const gridsAdapter: EntityAdapter<NormalizedGridEntity> = createEntityAdapter<NormalizedGridEntity>();

export const gridsReducer = createImmerReducer<GridsState>(
    gridsAdapter.getInitialState(),
    on(GridsActions.init, (state: GridsState, { entities }) => gridsAdapter.setAll(entities, state)),
    on(GridsActions.addedMany, (state: GridsState, { entities }) => gridsAdapter.addMany(entities, state)),
    on(GridsActions.added, GridsActions.created, GridsActions.copied, (state: GridsState, { entity }) => {
        return gridsAdapter.addOne(entity, state);
    }),
    on(GridsActions.remove, (state: GridsState, { entity }) => {
        return gridsAdapter.removeOne(entity.id, state);
        // return state;
    }),
    on(GridsActions.updateProperties, (state: GridsState, { update }) => {
        const grid = state.entities[update.id];
        if (!grid) {
            return state;
        }
        grid.props = { ...grid.props, ...update.changes.props };
        return state;
    }),
    on(GridsActions.updateManyProperties, (state: GridsState, { updates }) => {
        updates.forEach((update) => {
            const grid = state.entities[update.id];
            if (!grid) {
                return;
            }
            grid.props = { ...grid.props, ...update.changes.props };
        });
        return state;
    }),
    on(GridsActions.copyMany, (state: GridsState, { entities }) => {
        return gridsAdapter.addMany(entities, state);
    }),
    // react on widgets state change
    on(
        WidgetsLegacyActions.addWidgetToGrid,
        WidgetsLegacyActions.widgetCreatedToGrid,
        WidgetsLegacyActions.copyWidgetToGrid,
        (state: GridsState, { widget, gridCellIndex, index }) => {
            const grid = state.entities[widget.structureId];
            grid.children = Array.from({ length: getVariantMapList(grid.props.variant) }).map(
                (_, i) => grid.children[i] ?? []
            );
            grid.children[gridCellIndex].splice(index, 0, widget.id);

            return state;
        }
    ),
    // Widgets are placed by their own `parentId`; only a grid parent with a cell index is ours.
    on(
        WidgetsActions.created,
        WidgetsActions.added,
        WidgetsActions.copied,
        (state: GridsState, { widget, gridCellIndex, index }) => {
            const grid = state.entities[widget.parentId];
            if (!grid || gridCellIndex === undefined) {
                return state;
            }

            grid.children = Array.from({ length: getVariantMapList(grid.props.variant) }).map(
                (_, i) => grid.children[i] ?? []
            );
            grid.children[gridCellIndex].splice(index, 0, widget.id);

            return state;
        }
    ),
    on(WidgetsActions.removed, (state: GridsState, { widget }) => {
        const grid = state.entities[widget.parentId];
        if (!grid) {
            return state;
        }

        grid.children = grid.children.map((cell) => (cell ?? []).filter((id) => id !== widget.id));
        return state;
    }),
    on(WidgetsLegacyActions.removeWidgetFromGrid, (state: GridsState, { widget, index, gridCellIndex }) => {
        const widgetId = widget.id;
        const gridId = widget.structureId;

        // grid may not be at the expected id (stale structureId) and cells may be sparse,
        // so resolve defensively and guard every access before splicing.
        const grid =
            state.entities[gridId] ??
            Object.values(state.entities).find((item) => item?.children[gridCellIndex]?.includes(widgetId));

        grid?.children[gridCellIndex]?.splice(index, 1);

        return state;
    }),
    on(GridsActions.moveWidgetFromGridToColumn, (state: GridsState, { widgetId, prevParentId, prevGridCellIndex }) => {
        const grid = state.entities[prevParentId];

        grid.children = Array.from({ length: getVariantMapList(grid.props.variant) }).map(
            (_, i) => grid.children[i] ?? []
        );
        grid.children[prevGridCellIndex] = grid.children[prevGridCellIndex].filter((id) => id !== widgetId);

        return state;
    }),
    on(
        GridsActions.moveWidgetFromColumnToGrid,
        (state: GridsState, { widgetId, nextParentId, nextGridCellIndex, nextIndex }) => {
            const grid = state.entities[nextParentId];

            grid.children = Array.from({ length: getVariantMapList(grid.props.variant) }).map(
                (_, i) => grid.children[i] ?? []
            );
            grid.children[nextGridCellIndex].splice(nextIndex, 0, widgetId);
            return state;
        }
    ),
    on(
        GridsActions.moveWidgetFromGridToGrid,
        (
            state: GridsState,
            { widgetId, prevParentId, nextParentId, prevGridCellIndex, nextGridCellIndex, prevIndex, nextIndex }
        ) => {
            const prevGrid = state.entities[prevParentId];
            const nextGrid = state.entities[nextParentId];

            if (prevGrid) {
                prevGrid.children = Array.from({ length: getVariantMapList(prevGrid.props.variant) }).map(
                    (_, i) => prevGrid.children[i] ?? []
                );
                prevGrid.children[prevGridCellIndex]?.splice(prevIndex, 1);
            }

            if (nextGrid) {
                nextGrid.children = Array.from({ length: getVariantMapList(nextGrid.props.variant) }).map(
                    (_, i) => nextGrid.children[i] ?? []
                );
                nextGrid.children[nextGridCellIndex].splice(nextIndex, 0, widgetId);
            }

            return state;
        }
    )
);
